import { cn } from "@malla/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * Marca propia de Malla.
 *
 * The glyph is a constellation of linked nodes: a central node (sovereign
 * control) joined to three satellite nodes by edges that also close at
 * the perimeter, drawing a mesh. Evokes "private network, connected nodes,
 * you in control".
 *
 * Drawn with `currentColor` to inherit container color. ALWAYS accompanied
 * within a solid chip (`bg-accent` + `text-accent-foreground`) to
 * guarantee contrast in any theme, avoiding the gotcha of
 * `text-accent` on faint surfaces (invisible in several themes).
 */
function MallaMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-hidden="true"
    >
      {/* Aristas radiales hub → satélite*/}
      <path
        d="M16 16 16 6 M16 16 6.5 23.5 M16 16 25.5 23.5"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
      />
      {/* Aristas de perímetro (cierre de la malla)*/}
      <path
        d="M16 6 6.5 23.5 M16 6 25.5 23.5 M6.5 23.5 25.5 23.5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-opacity="0.42"
      />
      {/* Nodos satélite*/}
      <circle cx="16" cy="6" r="2.5" fill="currentColor" />
      <circle cx="6.5" cy="23.5" r="2.5" fill="currentColor" />
      <circle cx="25.5" cy="23.5" r="2.5" fill="currentColor" />
      {/* Nodo central (hub) con halo*/}
      <circle cx="16" cy="16" r="4.2" fill="currentColor" fill-opacity="0.28" />
      <circle cx="16" cy="16" r="2.9" fill="currentColor" />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-lg shadow-accent/25">
            <MallaMark class="h-5.5 w-5.5" />
          </div>
          <span class="text-xl font-bold tracking-tight text-foreground">
            Ma<span class="text-accent">lla</span>
          </span>
        </>
      ) : (
        <>
          <div class="mb-2 flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-xl shadow-accent/30 transition-transform duration-300 hover:scale-105 active:scale-95">
            <MallaMark class="h-9 w-9" />
          </div>
          <h1 class="text-3xl font-bold tracking-tight text-foreground">
            Ma<span class="text-accent">lla</span>
          </h1>
        </>
      )}
    </div>
  );
}
